import { faArrowLeft, faFileLines } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OrderItems } from '@spike/order-model';
import Pet from '@spike/pet-model';
import { formatPhoneNumber } from '@spike/phone-utils';
import { CheckInOutReport as CheckInOutReportModel, CheckInOutReportSection } from '@spike/report-model';
import {
    createCheckInOutReportThunk,
    getCheckInOutReportThunk,
    updateCheckInOutReportThunk
} from '@spike/reports-action';
import { showSuccess } from '@spike/notifications-action';
import Button from 'components/UI/V3/Button/Button';
import PetImage from 'components/UI/PetCard/PetImage';
import { FullWindowModal, FullWindowModalBody, FullWindowModalHeader } from 'components/UI/V3/Modals/FullWindowModal';
import Spinner from 'components/UI/V3/Spinner/Spinner';
import { useApiClientWrapper, useMarketplace } from 'hooks';
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { dispatch } from 'store';
import styles from './CheckInOutReport.module.scss';
import { faArrowLeftToBracket, faArrowRightToBracket } from '@fortawesome/pro-solid-svg-icons';

type ReportSide = 'checkIn' | 'checkOut';

const DEFAULT_REPORT_MODAL_Z_INDEX = 10003;

interface ConditionCategory {
    key: string;
    label: string;
    options: string[];
}

const CONDITION_CATEGORIES: ConditionCategory[] = [
    {
        key: 'eyes',
        label: 'Eyes',
        options: ['Normal', 'Watery', 'Discharge', 'Squinting', 'Swelling', 'Cloudy', 'Debris']
    },
    {
        key: 'ears',
        label: 'Ears',
        options: ['Normal', 'Wax', 'Odor', 'Irritation', 'Scratches', 'Swelling', 'Ear Flap', 'Pain', 'Fleas/Ticks']
    },
    { key: 'nose', label: 'Nose', options: ['Normal', 'Dry', 'Discharge', 'Cracked', 'Abrasion Blood'] },
    {
        key: 'teeth_gums',
        label: 'Teeth/Gums',
        options: ['Normal', 'Bad Breath', 'Plaque', 'Loose', 'Broken', 'Missing', 'Pale', 'Growth', 'Drooling', 'Blood']
    },
    {
        key: 'head_body',
        label: 'Head & Body',
        options: ['Normal', 'Lacerations', 'Irritation', 'Hair Loss', 'Growth', 'Swelling', 'Dirty', 'Fleas/Ticks']
    },
    {
        key: 'legs_paws',
        label: 'Legs & Paws',
        options: [
            'Normal',
            'Limping',
            'Stiffness',
            'Lacerations',
            'Objects',
            'Irritation',
            'Swelling',
            'Pads',
            'Nails',
            'Growth',
            'Fleas/Ticks',
            'Bleeding'
        ]
    },
    {
        key: 'tail',
        label: 'Tail',
        options: ['Normal', 'Growth', 'Fecal', 'Pain', 'Lacerations', 'Hair Loss', 'Fleas/Ticks', 'Bleeding']
    },
    {
        key: 'rectum',
        label: 'Rectum',
        options: ['Normal', 'Redness', 'Swelling', 'Scooting', 'Bleeding', 'Residue', 'Glands', 'Fleas/Ticks']
    },
    {
        key: 'genitals',
        label: 'Genitals',
        options: ['Normal', 'Irritation', 'Swelling', 'Discharge', 'Fleas/Ticks', 'Bleeding']
    },
    {
        key: 'observations',
        label: 'Observations',
        options: [
            'Fearful',
            'Anxious',
            'Timid',
            'Lethargic',
            'Panting',
            'Vocalization',
            'Diarrhea',
            'Vomiting',
            'Coughing',
            'Sneezing'
        ]
    }
];

const createEmptySection = (): CheckInOutReportSection =>
    CONDITION_CATEGORIES.reduce<CheckInOutReportSection>((section, category) => {
        section[category.key] = { conditions: [], notes: '' };
        return section;
    }, {});

const normalizeSection = (section: CheckInOutReportSection): CheckInOutReportSection => ({
    ...createEmptySection(),
    ...section
});

const createEmptyReport = (bookingId: number, petId: number): CheckInOutReportModel => ({
    bookingId,
    petId,
    checkIn: createEmptySection(),
    checkOut: createEmptySection()
});

const getReportContent = (report: CheckInOutReportModel) =>
    JSON.stringify({ checkIn: report.checkIn, checkOut: report.checkOut });

interface CheckInOutReportProps {
    pet: Pet;
    petId: number;
    bookingId: number;
    orderItems: OrderItems;
    zIndex?: number;
    onClose: () => void;
}

interface ReportColumnProps {
    side: ReportSide;
    title: string;
    date?: string;
    icon: IconDefinition;
    section: CheckInOutReportSection;
    disabled: boolean;
    // eslint-disable-next-line no-unused-vars
    onToggle: (_side: ReportSide, _category: string, _option: string) => void;
    // eslint-disable-next-line no-unused-vars
    onNotesChange: (_side: ReportSide, _category: string, _value: string) => void;
}

const ReportColumn: FunctionComponent<ReportColumnProps> = props => (
    <section className={styles.reportColumn}>
        <header className={styles.columnHeader}>
            <h2>
                <FontAwesomeIcon icon={props.icon} /> {props.title}
            </h2>
            <span>{props.date}</span>
        </header>
        {CONDITION_CATEGORIES.map(category => {
            const value = props.section[category.key] ?? { conditions: [], notes: '' };
            const selected = value.conditions;
            const needsNotes = selected.some(option => option !== 'Normal');
            return (
                <fieldset
                    key={category.key}
                    className={styles.category}
                    disabled={props.disabled}
                >
                    <legend>{category.label}</legend>
                    <div className={styles.chips}>
                        {category.options.map(option => (
                            <button
                                key={option}
                                type="button"
                                className={selected.includes(option) ? styles.chipSelected : styles.chip}
                                aria-pressed={selected.includes(option)}
                                onClick={() => props.onToggle(props.side, category.key, option)}
                            >
                                {option}
                            </button>
                        ))}
                    </div>
                    {(needsNotes || value.notes) && (
                        <textarea
                            className={styles.notes}
                            value={value.notes}
                            placeholder={`Add notes for ${category.label.toLowerCase()}...`}
                            onChange={event => props.onNotesChange(props.side, category.key, event.target.value)}
                        />
                    )}
                </fieldset>
            );
        })}
    </section>
);

export const CheckInOutReport: FunctionComponent<CheckInOutReportProps> = ({
    pet,
    petId,
    bookingId,
    orderItems,
    zIndex,
    onClose
}) => {
    const apiClientWrapper = useApiClientWrapper();
    const marketplace = useMarketplace();
    const [report, setReport] = useState<CheckInOutReportModel>(() => createEmptyReport(bookingId, petId));
    const persistedReport = useRef(report);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const isDirty = getReportContent(report) !== getReportContent(persistedReport.current);

    useEffect(() => {
        let active = true;
        setLoading(true);
        const initializeReport = async () => {
            const loaded = await dispatch(getCheckInOutReportThunk(apiClientWrapper, bookingId, petId));
            if (!active) return;
            if (loaded === null) {
                setLoading(false);
                return;
            }

            const initialized =
                loaded ??
                (await dispatch(createCheckInOutReportThunk(apiClientWrapper, createEmptyReport(bookingId, petId))));

            if (!active) return;
            if (initialized) {
                const normalizedReport = {
                    ...initialized,
                    checkIn: normalizeSection(initialized.checkIn),
                    checkOut: normalizeSection(initialized.checkOut)
                };
                persistedReport.current = normalizedReport;
                setReport(normalizedReport);
            }
            setLoading(false);
        };
        void initializeReport();
        return () => {
            active = false;
        };
    }, [bookingId, petId]);

    const appointments = orderItems.appointments;
    const dates = useMemo(() => {
        if (appointments.length === 0) return {};
        const sorted = [...appointments].sort(
            (a, b) => a.appointment.duration.from.valueOf() - b.appointment.duration.from.valueOf()
        );
        return {
            checkIn: sorted[0].appointment.duration.from.format('dddd, DD MMM YYYY'),
            checkOut: sorted[sorted.length - 1].appointment.duration.to.format('dddd, DD MMM YYYY')
        };
    }, [appointments]);

    const customer = appointments[0]?.appointment.customer;
    const countryId = marketplace.basics.address.country?.id;
    const customerPhone = customer?.phone && countryId ? formatPhoneNumber(customer.phone, countryId) : customer?.phone;
    const toggleOption = (side: ReportSide, category: string, option: string) => {
        setReport(current => {
            const categoryValue = current[side][category] ?? { conditions: [], notes: '' };
            const selected = categoryValue.conditions;
            let next: string[];
            if (option === 'Normal') next = selected.includes('Normal') ? [] : ['Normal'];
            else
                next = selected.includes(option)
                    ? selected.filter(value => value !== option)
                    : [...selected.filter(value => value !== 'Normal'), option];
            return {
                ...current,
                [side]: {
                    ...current[side],
                    [category]: { ...categoryValue, conditions: next }
                }
            };
        });
    };

    const changeNotes = (side: ReportSide, category: string, value: string) => {
        setReport(current => ({
            ...current,
            [side]: {
                ...current[side],
                [category]: {
                    ...(current[side][category] ?? { conditions: [] }),
                    notes: value
                }
            }
        }));
    };

    const saveReport = async () => {
        setSaving(true);
        const saveAction =
            report.id !== undefined
                ? updateCheckInOutReportThunk(apiClientWrapper, report)
                : createCheckInOutReportThunk(apiClientWrapper, report);
        const persisted = await dispatch(saveAction);
        setSaving(false);
        if (!persisted) return;

        const normalizedReport = {
            ...persisted,
            checkIn: normalizeSection(persisted.checkIn),
            checkOut: normalizeSection(persisted.checkOut)
        };
        persistedReport.current = normalizedReport;
        setReport(normalizedReport);
        dispatch(showSuccess('Report saved'));
    };

    return (
        <FullWindowModal
            className={styles.modal}
            zIndex={zIndex ?? DEFAULT_REPORT_MODAL_Z_INDEX}
        >
            <FullWindowModalHeader className={styles.modalHeader}>
                <div className={styles.headerContent}>
                    <button
                        type="button"
                        className={styles.backButton}
                        onClick={onClose}
                        aria-label="Close report"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </button>
                    <div className={styles.titleBlock}>
                        <h1>Health Check</h1>
                        <div className={styles.identityRow}>
                            <PetImage
                                petImage={pet.image}
                                petType={pet.type?.id}
                                className={styles.petImage}
                                imageClassName={styles.petImage}
                            />
                            <div className={styles.identityBlock}>
                                <span className={styles.identityLabel}>Pet</span>
                                <strong>{pet.name}</strong>
                                <span>
                                    {pet.type?.singular ?? 'Pet'} · {pet.breed?.name ?? ''}
                                </span>
                            </div>
                            <div className={styles.identityBlock}>
                                <span className={styles.identityLabel}>Pet Parent</span>
                                <strong>
                                    {pet.clientName ?? `${customer?.firstName ?? ''} ${customer?.lastName ?? ''}`}
                                </strong>
                                <span>{customerPhone}</span>
                            </div>
                        </div>
                    </div>
                    <Button
                        variant="green"
                        size="large"
                        label="Save Report"
                        isLoading={saving}
                        disabled={loading || saving || !isDirty}
                        onClick={saveReport}
                    />
                </div>
            </FullWindowModalHeader>
            <FullWindowModalBody className={styles.modalBody}>
                {loading ? (
                    <div className={styles.loadingContainer}>
                        <Spinner size={48} />
                    </div>
                ) : (
                    <div className={styles.reportGrid}>
                        <ReportColumn
                            side="checkIn"
                            title="Check-In Report"
                            date={dates.checkIn}
                            icon={faArrowLeftToBracket}
                            section={report.checkIn}
                            disabled={saving}
                            onToggle={toggleOption}
                            onNotesChange={changeNotes}
                        />
                        <ReportColumn
                            side="checkOut"
                            title="Check-Out Report"
                            date={dates.checkOut}
                            icon={faArrowRightToBracket}
                            section={report.checkOut}
                            disabled={saving}
                            onToggle={toggleOption}
                            onNotesChange={changeNotes}
                        />
                    </div>
                )}
            </FullWindowModalBody>
        </FullWindowModal>
    );
};

export const CheckInOutReportButton: FunctionComponent<{ onClick: () => void }> = ({ onClick }) => (
    <Button
        variant="black"
        size="small"
        leftIcon={faFileLines}
        label="Health Check"
        className={styles.triggerButton}
        onClick={onClick}
    />
);

export default CheckInOutReport;
